import { z } from 'zod';

import { defineRoute } from '../http/route-contract';
import { IsoDateTimeSchema } from '../primitives/datetime';
import { PositiveDecimalSchema } from '../primitives/decimal';
import { SettingsVersionIdSchema, SubscriptionIdSchema } from '../primitives/ids';
import { MoneySchema } from '../primitives/money';

import { AccountLimitsVersionSchema } from './limits';
import {
  checkSuspensionCause,
  SubscriptionPathParamsSchema,
  SubscriptionStatusSchema,
  SuspensionReasonCodeSchema,
} from './subscriptions';

/** Size: risk percent (recommended, shown first) or fixed lot; only one active (FXM-02 §4). */
export const SizingSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('RISK_PERCENT'),
    riskPercent: PositiveDecimalSchema.describe('Percent of equity lost at the SL.'),
  }),
  z.object({ mode: z.literal('FIXED_LOT'), lots: PositiveDecimalSchema }),
]);
/** Sizing. */
export type Sizing = z.infer<typeof SizingSchema>;

/** Automatic or confirmation; bounds of the expiry are open (Q24), 5 min is the UI's suggestion (FXM-02 §4). */
export const ExecutionModeSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('AUTOMATIC') }),
  z.object({ mode: z.literal('CONFIRMATION'), expiryMinutes: z.int().min(1) }),
]);
/** Execution mode. */
export type ExecutionMode = z.infer<typeof ExecutionModeSchema>;

const settingsShape = {
  sizing: SizingSchema,
  copySignalsWithoutStopLoss: z
    .boolean()
    .describe('Off unless the trader turns it on; never defaulted (I7, FXM-02 §4).'),
  fallbackStopLossPips: PositiveDecimalSchema.nullable().describe(
    'Used only to compute the size, no SL is placed (FXM-02 §6 case 10). No default (Q2).',
  ),
  execution: ExecutionModeSchema,
  maxOpenPositions: z.int().min(1).nullable().describe('null = limit disabled (default, I7).'),
};

/** Settings of one channel as returned by the API (tolerant to new fields). */
export const ChannelSettingsSchema = z.object(settingsShape);
/** Channel settings. */
export type ChannelSettings = z.infer<typeof ChannelSettingsSchema>;

/**
 * Body of PUT /subscriptions/{id}/settings: strict, every key required (I7). In risk mode, copying
 * signals without SL needs a fallback SL distance, or validation is refused (FXM-02 §6 case 10).
 * `expectedVersion` is the `version` of the active settings the trader saw, null for a channel
 * never configured: another device having saved meanwhile answers 409 (B-D16, C1 spec R18).
 */
export const SaveSettingsRequestSchema = z
  .strictObject({
    ...settingsShape,
    expectedVersion: z
      .int()
      .min(1)
      .nullable()
      .describe(
        'The `version` of the active settings the trader saw, or null when never configured. A different current version answers 409 SETTINGS_VERSION_CONFLICT.',
      ),
  })
  .superRefine((settings, ctx) => {
    if (
      settings.sizing.mode === 'RISK_PERCENT' &&
      settings.copySignalsWithoutStopLoss &&
      settings.fallbackStopLossPips === null
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['fallbackStopLossPips'],
        message:
          'Fallback SL required in risk mode when signals without SL are copied (FXM-02 §6 case 10)',
      });
    }
  });
/** PUT settings body. */
export type SaveSettingsRequest = z.infer<typeof SaveSettingsRequestSchema>;

/** One immutable settings version (master X10): the setting active at any trade time can be found. */
export const SettingsVersionSchema = z.object({
  id: SettingsVersionIdSchema,
  subscriptionId: SubscriptionIdSchema,
  version: z
    .int()
    .min(1)
    .describe(
      'Proposal, to be validated: 1 for the first settings of the channel, then +1 per version. The client sends it back as `expectedVersion`.',
    ),
  createdAt: IsoDateTimeSchema,
  settings: ChannelSettingsSchema,
});
/** Settings version. */
export type SettingsVersion = z.infer<typeof SettingsVersionSchema>;

/** Response of GET /subscriptions/{id}/settings. */
export const SubscriptionSettingsResponseSchema = z
  .object({
    subscriptionId: SubscriptionIdSchema,
    status: SubscriptionStatusSchema,
    suspensionReasonCode: SuspensionReasonCodeSchema,
    active: SettingsVersionSchema.nullable().describe(
      'null when never configured: nothing is copied (FXM-02 §7).',
    ),
    recommended: ChannelSettingsSchema.nullable().describe(
      "Affiliate's recommended setting, offered as an editable starting point, never applied by itself (FXM-02 §4, I7).",
    ),
    history: z.array(SettingsVersionSchema).describe('Newest first.'),
    openPositionCount: z
      .int()
      .min(0)
      .describe(
        'Open positions on this channel: above 0, E7 says they keep their size (M2 R15, FXM-02 §6 case 5).',
      ),
    accountLimits: AccountLimitsVersionSchema.nullable().describe(
      'The account-wide limits as last saved; null when no limit was ever saved (I7).',
    ),
  })
  .superRefine(checkSuspensionCause);
/** GET settings response. */
export type SubscriptionSettingsResponse = z.infer<typeof SubscriptionSettingsResponseSchema>;

/**
 * Body of POST …/settings/preview: the sizing, and the draft's fallback SL distance (P9). Every
 * key is required (C1 R11): `fallbackStopLossPips` is null when signals without SL are not
 * copied or no valid distance is typed; then the response's `fallbackStopLoss` is null.
 */
export const PreviewSettingsRequestSchema = z.strictObject({
  sizing: SizingSchema,
  fallbackStopLossPips: PositiveDecimalSchema.nullable().describe(
    "The draft's fallback SL distance, sent only while signals without SL are copied; a whole number of pips from 1 to 10 000 (M2 R20), else null; then fallbackStopLoss is null. P9, Proposal, to be validated.",
  ),
});
/** Preview body. */
export type PreviewSettingsRequest = z.infer<typeof PreviewSettingsRequestSchema>;

/**
 * Why the example is on `referenceSymbol` (P9; Q32, Q258, provisional): the channel's most
 * frequent instrument over its last 50 signals (`CHANNEL`), EURUSD because the channel has no
 * signal yet (`DEFAULT`), or EURUSD because the account gives no pip value for the channel's
 * instrument (`CHANNEL_INSTRUMENT_NOT_PRICED`).
 */
export const ReferenceSourceSchema = z.enum([
  'CHANNEL',
  'DEFAULT',
  'CHANNEL_INSTRUMENT_NOT_PRICED',
]);
/** Reference source of the worked example. */
export type ReferenceSource = z.infer<typeof ReferenceSourceSchema>;

const SymbolSchema = z.string().min(1).max(32);

/** What a signal without SL would lose at the fallback distance (P9 rule 3). */
export const FallbackStopLossPreviewSchema = z.object({
  pips: PositiveDecimalSchema.describe('The fallbackStopLossPips of the request.'),
  estimatedLoss: MoneySchema.describe(
    'Fixed lot: lots x pips x the pip value of referenceSymbol. Risk percent: the risk budget, equal to the preview estimatedLoss (the size adapts to the distance).',
  ),
});
/** Fallback SL example of the preview. */
export type FallbackStopLossPreview = z.infer<typeof FallbackStopLossPreviewSchema>;

/**
 * Worked example before validation: loss for a 30-pip SL on the current equity (FXM-02 §4, §7),
 * on the channel's instrument when the account can price it (P9). `channelSymbol` is null
 * exactly when `DEFAULT`, and a `CHANNEL` example is on `channelSymbol` (the API checks both).
 */
export const SettingsPreviewResponseSchema = z
  .object({
    stopLossPips: PositiveDecimalSchema.describe('30 per FXM-02 §4.'),
    referenceSymbol: SymbolSchema.describe(
      "Instrument of the example: the channel's most frequent one when the account can price it, else EURUSD (P9; Q32, Q258, provisional).",
    ),
    referenceSource: ReferenceSourceSchema,
    channelSymbol: SymbolSchema.nullable().describe(
      "The channel's most frequent instrument; null without any signal (P9).",
    ),
    equity: MoneySchema,
    estimatedLoss: MoneySchema.describe('In the account currency (Q27).'),
    fallbackStopLoss: FallbackStopLossPreviewSchema.nullable().describe(
      'Set exactly when the request has fallbackStopLossPips (P9).',
    ),
  })
  .superRefine((preview, ctx) => {
    if ((preview.referenceSource === 'DEFAULT') !== (preview.channelSymbol === null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['channelSymbol'],
        message: 'channelSymbol is null exactly when referenceSource is DEFAULT (P9)',
      });
    }
    if (
      preview.referenceSource === 'CHANNEL' &&
      preview.referenceSymbol !== preview.channelSymbol
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['referenceSymbol'],
        message: 'a CHANNEL example is on channelSymbol (P9)',
      });
    }
  });
/** Preview response. */
export type SettingsPreviewResponse = z.infer<typeof SettingsPreviewResponseSchema>;

const ENGINE =
  'FXM-02 settings vs allocation_method / risk_factor of the engine (RFX analysis §8).';

/** GET /subscriptions/{subscriptionId}/settings. */
export const getSubscriptionSettingsRoute = defineRoute({
  operationId: 'getSubscriptionSettings',
  method: 'GET',
  path: '/subscriptions/{subscriptionId}/settings',
  tag: 'Settings',
  summary: 'Get the settings of a channel',
  description: 'Active version, affiliate recommendation and version history.',
  source: 'technical spec §6; FXM-02 §4, §5; US-06, US-10',
  auth: 'bearer',
  provisional: { questions: ['Q37'], note: ENGINE },
  params: SubscriptionPathParamsSchema,
  success: {
    kind: 'json',
    status: 200,
    body: { name: 'SubscriptionSettingsResponse', schema: SubscriptionSettingsResponseSchema },
  },
});

/** POST /subscriptions/{subscriptionId}/settings/preview. Writes nothing. */
export const previewSubscriptionSettingsRoute = defineRoute({
  operationId: 'previewSubscriptionSettings',
  method: 'POST',
  path: '/subscriptions/{subscriptionId}/settings/preview',
  tag: 'Settings',
  summary: 'Preview the loss for a 30-pip stop loss',
  description: 'Computes the worked example on the current equity; writes nothing.',
  source: 'technical spec §6; FXM-02 §4, §7; US-10',
  auth: 'bearer',
  provisional: {
    questions: ['Q27', 'Q32', 'Q258'],
    note: 'currency and reference instrument of the example are open.',
  },
  params: SubscriptionPathParamsSchema,
  body: { name: 'PreviewSettingsRequest', schema: PreviewSettingsRequestSchema },
  success: {
    kind: 'json',
    status: 200,
    body: { name: 'SettingsPreviewResponse', schema: SettingsPreviewResponseSchema },
  },
  errors: [
    { status: 409, reasonCode: 'TRADING_ACCOUNT_NOT_CONNECTED' },
    { status: 503, reasonCode: 'RISK_PREVIEW_UNAVAILABLE' },
  ],
});

/** PUT /subscriptions/{subscriptionId}/settings. Creates a version, never updates one (master X10). */
export const saveSubscriptionSettingsRoute = defineRoute({
  operationId: 'saveSubscriptionSettings',
  method: 'PUT',
  path: '/subscriptions/{subscriptionId}/settings',
  tag: 'Settings',
  summary: 'Validate the settings of a channel',
  description:
    'Creates a new version; applies to the next signals, open positions keep their size (FXM-02 §6 case 5).',
  source: 'technical spec §6; FXM-02 §4, §6, §7; US-06, US-10 to US-12',
  auth: 'bearer',
  provisional: { questions: ['Q2', 'Q24', 'Q37'], note: ENGINE },
  params: SubscriptionPathParamsSchema,
  body: { name: 'SaveSettingsRequest', schema: SaveSettingsRequestSchema },
  success: {
    kind: 'json',
    status: 201,
    body: { name: 'SettingsVersion', schema: SettingsVersionSchema },
  },
  errors: [
    { status: 400, reasonCode: 'SETTINGS_FALLBACK_SL_REQUIRED' },
    { status: 409, reasonCode: 'SETTINGS_VERSION_CONFLICT' },
  ],
});
